import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";

import { toaster } from "@/components/ui/toaster";
import { makeRequest } from "@/network/api";

type AdminUser = {
  id: string;
  walletAddress: string;
  role: "ADMIN" | "USER";
  roleNote: string | null;
  createdAt: string;
  updatedAt: string | null;
};

export const UserAdmin = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newAdminAddress, setNewAdminAddress] = useState("");
  const [newAdminNote, setNewAdminNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetchAdminUsers = async () => {
    try {
      setIsLoading(true);
      const response = await makeRequest("/admin/users");
      const responseData = await response.json();
      setAdminUsers(responseData.data.adminUsers);
    } catch (error) {
      console.error("Error fetching admin users:", error);
      setError("Failed to load admin users");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data loading is synchronized with mounting this admin view.
  /* oxlint-disable react/set-state-in-effect */
  useEffect(() => {
    fetchAdminUsers();
  }, []);
  /* oxlint-enable react/set-state-in-effect */

  const handleAddAdmin = async () => {
    if (!newAdminAddress.trim()) {
      toaster.create({
        type: "error",
        title: "Error",
        description: "Please enter a wallet address",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await makeRequest("/admin/users/setAdmin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          walletAddress: newAdminAddress.trim(),
          roleNote: newAdminNote.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add admin");
      }

      toaster.create({
        type: "success",
        title: "Success",
        description: "Admin user added successfully",
      });

      // Clear form
      setNewAdminAddress("");
      setNewAdminNote("");

      // Refetch admin users
      await fetchAdminUsers();
    } catch (error) {
      toaster.create({
        type: "error",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to add admin",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAdmin = async (walletAddress: string) => {
    if (
      !confirm(
        `Are you sure you want to remove admin role from ${walletAddress}?`,
      )
    ) {
      return;
    }

    try {
      const response = await makeRequest("/admin/users/removeAdmin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          walletAddress,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remove admin");
      }

      toaster.create({
        type: "success",
        title: "Success",
        description: "Admin role removed successfully",
      });

      // Refetch admin users
      await fetchAdminUsers();
    } catch (error) {
      toaster.create({
        type: "error",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to remove admin",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="50vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} maxW="1200px" mx="auto">
        <Heading mb={6}>User Admin</Heading>
        <Card.Root p={4}>
          <Card.Body>
            <Text color="red.500">{error}</Text>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  }

  return (
    <Box p={0} maxW="1200px" mx="auto">
      <Heading mb={6}>User Admin</Heading>

      {/* Add New Admin Form */}
      <Card.Root p={4} mb={6}>
        <Card.Header>
          <Heading size="md">Add New Admin</Heading>
        </Card.Header>
        <Card.Body>
          <VStack gap={4} alignItems="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Wallet Address *
              </Text>
              <Input
                placeholder="0x..."
                value={newAdminAddress}
                onChange={(e) => setNewAdminAddress(e.target.value)}
                disabled={isSubmitting}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Note (optional)
              </Text>
              <Textarea
                placeholder="Add a note about this admin user..."
                value={newAdminNote}
                onChange={(e) => setNewAdminNote(e.target.value)}
                disabled={isSubmitting}
                rows={3}
              />
            </Box>
            <Button
              onClick={handleAddAdmin}
              loading={isSubmitting}
              loadingText="Adding..."
              colorScheme="blue"
              alignSelf="flex-start"
            >
              Add Admin
            </Button>
          </VStack>
        </Card.Body>
      </Card.Root>

      {/* Admin Users List */}
      <Card.Root p={4}>
        <Card.Header>
          <Heading size="md">Current Admin Users ({adminUsers.length})</Heading>
        </Card.Header>
        <Card.Body>
          {adminUsers.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={8}>
              No admin users found
            </Text>
          ) : (
            <VStack gap={4} alignItems="stretch">
              {adminUsers.map((user) => (
                <Card.Root key={user.id} variant="outline">
                  <Card.Body>
                    <Flex
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <VStack alignItems="flex-start" gap={2} flex={1}>
                        <HStack>
                          <Text
                            fontWeight="bold"
                            fontSize="sm"
                            color="blue.600"
                          >
                            {user.walletAddress}
                          </Text>
                        </HStack>
                        {user.roleNote && (
                          <Text fontSize="sm" color="gray.600">
                            <strong>Note:</strong> {user.roleNote}
                          </Text>
                        )}
                        <Text fontSize="xs" color="gray.500">
                          Added: {formatDate(user.createdAt)}
                          {user.updatedAt &&
                            user.updatedAt !== user.createdAt && (
                              <> • Updated: {formatDate(user.updatedAt)}</>
                            )}
                        </Text>
                      </VStack>
                      <IconButton
                        aria-label="Remove admin"
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleRemoveAdmin(user.walletAddress)}
                      >
                        <FiTrash2 />
                      </IconButton>
                    </Flex>
                  </Card.Body>
                </Card.Root>
              ))}
            </VStack>
          )}
        </Card.Body>
      </Card.Root>
    </Box>
  );
};
